// @ts-check
/**
 * Small pure helpers for the WhatsApp admin screens: labels for message states, the 24 hour reply
 * window, phone display, and template placeholder handling.
 *
 * The enum values below mirror /shared/api-constants.js (WHATSAPP_*). The admin build cannot import
 * that file (Create React App refuses imports from outside src), so utils/whatsapp.test.js compares
 * these copies to the shared file and fails if they drift.
 */
import { countryForPhone } from './countries';

export const WHATSAPP_MESSAGE_STATUS = Object.freeze({
  QUEUED: 'queued',
  SENDING: 'sending',
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
  FAILED: 'failed',
  SKIPPED: 'skipped',
});

export const WHATSAPP_CAMPAIGN_STATUS = Object.freeze({
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  SENDING: 'sending',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
});

export const WHATSAPP_TEMPLATE_STATUS = Object.freeze({
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
});

export const WHATSAPP_MESSAGE_SOURCE = Object.freeze({
  WALK_IN_LEAD: 'walk_in_lead',
  CUSTOMER: 'customer',
  ATELIER_CONTACT: 'atelier_contact',
  CSV: 'csv',
  ORDER: 'order',
  INBOX: 'inbox',
  PHONE_APP: 'phone_app',
  WHOLESALE: 'wholesale',
});

export const WHATSAPP_SKIP_REASON = Object.freeze({
  INVALID_NUMBER: 'invalid_number',
  OPTED_OUT: 'opted_out',
  DUPLICATE: 'duplicate',
  TEMPLATE_UNAVAILABLE: 'template_unavailable',
  MISSING_PHONE: 'missing_phone',
  MISSING_PERSONALIZATION: 'missing_personalization',
});

export const WHATSAPP_TRIGGER_KEY = Object.freeze({
  ORDER_CONFIRMED: 'order_confirmed',
  ORDER_SHIPPED: 'order_shipped',
  ORDER_DELIVERED: 'order_delivered',
  LEAD_CAPTURED: 'lead_captured',
});

/** Structured error codes the retry-failed endpoint's 409 detail can carry (see err.code). */
export const WHATSAPP_RETRY_FAILED_CODE = Object.freeze({
  ONLY_UNCERTAIN: 'only_uncertain', // every failed row is one that may already have been delivered
});

/** Structured error codes the inbox reply endpoint's 409 detail can carry (see err.code). */
export const WHATSAPP_INBOX_REPLY_CODE = Object.freeze({
  WINDOW_CLOSED: 'window_closed', // the 24 hour customer-service window has closed
  SUPPRESSED: 'suppressed', // the customer opted out
  SEND_DISABLED: 'send_disabled', // WHATSAPP_SEND_ENABLED is off on this environment
  WINDOW_OPEN: 'window_open', // send-template only: the customer wrote recently, so reply normally
  TEMPLATE_UNAVAILABLE: 'template_unavailable', // send-template only: not approved or gone
  INVALID_PARAMS: 'invalid_params', // send-template only: wrong number of variables
  UNSUPPORTED_TEMPLATE: 'unsupported_template', // send-template only: has a dynamic-URL button
});

const MB = 1024 * 1024;
/**
 * Files WhatsApp accepts, by extension: kind, mime type and Meta's size cap in bytes. These caps are
 * checked against the UPLOADED file (after client image compression / server video compression), not
 * the raw pick; whatsappMediaPrep.js holds the larger raw caps. Documents are 25 MB, lower than Meta's
 * 100 MB, because of our upload path (see backend/utils/media_types.py).
 */
const WHATSAPP_MEDIA_BY_EXT = {
  jpg: ['image', 'image/jpeg', 5 * MB],
  jpeg: ['image', 'image/jpeg', 5 * MB],
  png: ['image', 'image/png', 5 * MB],
  mp4: ['video', 'video/mp4', 16 * MB],
  aac: ['audio', 'audio/aac', 16 * MB],
  amr: ['audio', 'audio/amr', 16 * MB],
  mp3: ['audio', 'audio/mpeg', 16 * MB],
  m4a: ['audio', 'audio/mp4', 16 * MB],
  ogg: ['audio', 'audio/ogg', 16 * MB],
  pdf: ['document', 'application/pdf', 25 * MB],
  doc: ['document', 'application/msword', 25 * MB],
  docx: ['document', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 25 * MB],
  xls: ['document', 'application/vnd.ms-excel', 25 * MB],
  xlsx: ['document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 25 * MB],
  txt: ['document', 'text/plain', 25 * MB],
};

/** The file picker `accept` list for WhatsApp attachments. */
export const WHATSAPP_MEDIA_ACCEPT = Object.keys(WHATSAPP_MEDIA_BY_EXT)
  .map((ext) => `.${ext}`)
  .join(',');

/**
 * Checks a file against what WhatsApp accepts, by extension and size, before anything is sent.
 * @param {string} name file name
 * @param {number} size size in bytes
 * @returns {{ok: true, kind: 'image' | 'video' | 'audio' | 'document', mime: string} | {ok: false, message: string}}
 */
export const checkWhatsAppMedia = (name, size) => {
  const ext = String(name || '').includes('.') ? String(name).split('.').pop().toLowerCase() : '';
  const entry = WHATSAPP_MEDIA_BY_EXT[ext];
  if (!entry) return { ok: false, message: 'WhatsApp does not accept this file type.' };
  const [kind, mime, cap] = entry;
  if (size > cap) return { ok: false, message: `This ${kind} is over the ${cap / MB} MB limit.` };
  return { ok: true, kind, mime };
};

const STATUS_LABELS = {
  [WHATSAPP_MESSAGE_STATUS.QUEUED]: 'Queued',
  [WHATSAPP_MESSAGE_STATUS.SENDING]: 'Sending',
  [WHATSAPP_MESSAGE_STATUS.SENT]: 'Sent',
  [WHATSAPP_MESSAGE_STATUS.DELIVERED]: 'Delivered',
  [WHATSAPP_MESSAGE_STATUS.READ]: 'Read',
  [WHATSAPP_MESSAGE_STATUS.FAILED]: 'Failed',
  [WHATSAPP_MESSAGE_STATUS.SKIPPED]: 'Skipped',
};

const SKIP_REASON_LABELS = {
  [WHATSAPP_SKIP_REASON.INVALID_NUMBER]: 'Invalid number',
  [WHATSAPP_SKIP_REASON.OPTED_OUT]: 'Opted out',
  [WHATSAPP_SKIP_REASON.DUPLICATE]: 'Duplicate',
  [WHATSAPP_SKIP_REASON.TEMPLATE_UNAVAILABLE]: 'Template unavailable',
  [WHATSAPP_SKIP_REASON.MISSING_PHONE]: 'No phone number',
  [WHATSAPP_SKIP_REASON.MISSING_PERSONALIZATION]: 'No linked product for the image/link',
};

/** "on_hold" -> "On hold", for values this build does not know yet. */
const humanize = (/** @type {string | null | undefined} */ value) => {
  const text = String(value ?? '')
    .replace(/_/g, ' ')
    .trim();
  return text ? text[0].toUpperCase() + text.slice(1) : '';
};

/** @param {string | null | undefined} status */
export const statusLabel = (status) => STATUS_LABELS[/** @type {string} */ (status)] ?? humanize(status);

/**
 * The outcome counts shown on campaign cards and the campaign header, in display order, with labels.
 * Shared so the list and the detail view always show the same statuses the same way.
 */
export const CAMPAIGN_STATUS_COUNT_ORDER = Object.freeze(
  [
    WHATSAPP_MESSAGE_STATUS.SENT,
    WHATSAPP_MESSAGE_STATUS.DELIVERED,
    WHATSAPP_MESSAGE_STATUS.READ,
    WHATSAPP_MESSAGE_STATUS.FAILED,
  ].map((status) => Object.freeze({ status, label: statusLabel(status) })),
);

/** @param {string | null | undefined} reason */
export const skipReasonLabel = (reason) => SKIP_REASON_LABELS[/** @type {string} */ (reason)] ?? humanize(reason);

const WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Whether free-form replies are still allowed: WhatsApp only permits them within 24 hours of the
 * customer's last message. No inbound message (or an unreadable time) means closed.
 * @param {string | number | Date | null | undefined} lastInboundAt
 * @param {number} [now]
 */
export function windowOpen(lastInboundAt, now = Date.now()) {
  if (lastInboundAt === null || lastInboundAt === undefined || lastInboundAt === '') return false;
  const sentAt = new Date(lastInboundAt).getTime();
  if (Number.isNaN(sentAt)) return false;
  return now - sentAt < WINDOW_MS;
}

/**
 * "+919876543210" -> "+91 98765 43210". India groups 5+5, +1 groups 3+3+4, others get the dial code
 * then the rest. Anything that is not an E.164 number with a known dial code comes back unchanged.
 * @param {string | null | undefined} e164
 */
export function formatPhoneDisplay(e164) {
  const text = String(e164 ?? '').trim();
  const country = countryForPhone(text);
  if (!country) return text;
  const national = text
    .slice(1)
    .replace(/\D/g, '')
    .slice(country.dial.length - 1);
  if (country.dial === '+91' && national.length === 10) return `+91 ${national.slice(0, 5)} ${national.slice(5)}`;
  if (country.dial === '+1' && national.length === 10) {
    return `+1 ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
  }
  return `${country.dial} ${national}`;
}

const NAME_CHOICES = [
  { value: 'first_name', label: 'First name' },
  { value: 'full_name', label: 'Full name' },
  { value: 'sender_name', label: 'Sender name' },
  { value: 'sender_position', label: "Sender's position" },
];

/** Placeholders that make sense per audience source; must stay within backend VARIABLE_SOURCES. */
const SOURCE_CHOICES = {
  walk_in_lead: [
    ...NAME_CHOICES,
    { value: 'event_name', label: 'Event name' },
    { value: 'event_venue', label: 'Event venue' },
    { value: 'event_date', label: 'Visit date' },
    { value: 'product_name', label: "Customer's preferred product" },
    { value: 'product_image_url', label: "Preferred product's image" },
    { value: 'product_url', label: "Preferred product's page link" },
    { value: 'product_slug', label: "Preferred product's link ending" },
    { value: 'style', label: 'Style they tried' },
    { value: 'size_tried', label: 'Size they tried' },
  ],
  atelier_contact: [...NAME_CHOICES, { value: 'custom:company_name', label: 'Company name' }],
};

/**
 * @param {string | null | undefined} source audience source (walk_in_lead, customer, atelier_contact, csv)
 * @returns {{value: string, label: string}[]}
 */
export const variableChoicesFor = (source) => SOURCE_CHOICES[/** @type {'walk_in_lead'} */ (source)] ?? NAME_CHOICES;

const PLACEHOLDER = /\{\{\s*(\d+)\s*\}\}/g;

/**
 * How many variable slots a template body needs: the HIGHEST {{n}} index (0 when there are none),
 * the same rule as the backend's variable_count. Repeats do not matter and a gap counts, so
 * "{{1}} {{3}}" needs 3 slots.
 * @param {string | null | undefined} body
 */
export const templateVariableCount = (body) =>
  Math.max(0, ...[...String(body ?? '').matchAll(PLACEHOLDER)].map((m) => Number(m[1])));

/**
 * Fills {{n}} with params[n-1]. A placeholder with no param renders as an empty string, never as
 * raw braces (same rule as the backend's render_preview_text).
 * @param {string | null | undefined} body
 * @param {string[] | null | undefined} params
 */
export const fillTemplate = (body, params) =>
  String(body ?? '').replace(PLACEHOLDER, (_match, n) => (params ?? [])[Number(n) - 1] ?? '');

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "2026-09-20T10:00:00Z" -> "20 Sep 2026" in the browser's local day. Spelled out by hand because
 * toLocaleDateString gives "Sept" on some engines.
 * @param {string | null | undefined} iso
 */
export function formatShortDate(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * "2026-09-20T09:05:00" (local) -> "20 Sep 2026, 09:05": the short date plus a 24 hour local time.
 * @param {string | null | undefined} iso
 */
export function formatShortDateTime(iso) {
  const day = formatShortDate(iso);
  if (!day) return '';
  const date = new Date(/** @type {string} */ (iso));
  const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
  return `${day}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Plain-English label for a trigger placeholder value source (see backend TRIGGER_PLACEHOLDERS). */
const PLACEHOLDER_LABELS = {
  first_name: 'First name',
  full_name: 'Full name',
  event_name: 'Event name',
  event_venue: 'Event venue',
  event_date: 'Visit date',
  product_name: "Customer's preferred product",
  product_image_url: "Preferred product's image",
  product_url: "Preferred product's page link",
  product_slug: "Preferred product's link ending",
  sender_name: 'Sender name',
  sender_position: "Sender's position",
  style: 'Style they tried',
  size_tried: 'Size they tried',
  'custom:order_number': 'Order number',
  'custom:tracking_number': 'Tracking number',
  'custom:carrier': 'Carrier',
  'custom:tracking_url': 'Tracking link',
};

/**
 * "custom:order_number" -> "Order number"; falls back to a humanized reading of the source for one
 * this build does not know yet (the "custom:" prefix is stripped first for a readable fallback).
 * @param {string | null | undefined} source
 */
export const placeholderLabel = (source) =>
  PLACEHOLDER_LABELS[/** @type {string} */ (source)] ?? humanize(String(source ?? '').replace(/^custom:/, ''));

/** Placeholders with a more specific sample than the generic Priya/Sample-N convention (Task 14's wizard). */
const SPECIFIC_TRIGGER_SAMPLES = { event_name: 'Your event', style: 'Style', size_tried: 'M' };

/**
 * Sample preview values for a trigger's mapped slots, one per slot in order: a placeholder like
 * event_name/style/size_tried always gets its specific sample; otherwise the first slot is "Priya"
 * and the rest are "Sample 2", "Sample 3", ...; an unmapped slot renders as '' (never a fake sample
 * for a value that is not actually configured yet).
 * @param {Record<string, string>} variableMap slot index ("1", "2", ...) -> placeholder source
 * @param {string[]} slots ordered slot indexes, e.g. ["1", "2"]
 */
export const triggerSampleParams = (variableMap, slots) =>
  slots.map((slot, index) => {
    const source = variableMap?.[slot];
    if (!source) return '';
    if (source in SPECIFIC_TRIGGER_SAMPLES) return SPECIFIC_TRIGGER_SAMPLES[source];
    return index === 0 ? 'Priya' : `Sample ${index + 1}`;
  });

/**
 * Campaigns list display order: every `scheduled` campaign first (soonest `scheduled_at` first),
 * then everything else in the order the backend already returned (created_at desc).
 * @param {Array<{status: string, scheduled_at?: string | null}>} campaigns
 */
export function sortCampaignsForDisplay(campaigns) {
  const scheduled = campaigns.filter((c) => c.status === WHATSAPP_CAMPAIGN_STATUS.SCHEDULED);
  const rest = campaigns.filter((c) => c.status !== WHATSAPP_CAMPAIGN_STATUS.SCHEDULED);
  scheduled.sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
  return [...scheduled, ...rest];
}
